import { useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Businesshours } from '@gql/base-types';
import { useGetLocationBusinessHoursQuery } from '../../settings/gql/businessHours.generated';
import { useValetSelfManaged } from '@hopdrive/feature-portal-products';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface TimelineBusinessHours {
  openTime: string; // Format: HH:mm:ss (e.g., "09:00:00")
  closeTime: string; // Format: HH:mm:ss (e.g., "17:00:00")
  isClosed: boolean;
  closureName?: string;
  isUsingDefaults: boolean; // Whether using default 6 AM - 8 PM fallback
}

const DEFAULT_OPEN_TIME = '06:00:00';
const DEFAULT_CLOSE_TIME = '20:00:00';

/**
 * Check if a specific date matches a closure
 */
const isDateClosed = (
  date: string,
  closures: Businesshours[],
  timezoneStr: string
): { isClosed: boolean; closureName?: string } => {
  // Note: .add(12, 'hours') is a workaround for timezone handling to ensure the correct day is selected
  // This must match the calculation used elsewhere in the codebase
  const targetDate = dayjs(date).add(12, 'hours').tz(timezoneStr);
  const targetDay = targetDate.date();
  const targetMonth = targetDate.month() + 1; // dayjs months are 0-indexed
  const targetYear = targetDate.year();
  const targetDayOfWeek = targetDate.day(); // 0=Sunday, 6=Saturday

  for (const closure of closures) {
    // Check one-off closures (specific dates) - only if is_open is false
    // One-off records with is_open: true are modified hours, not closures
    if (closure.type === 'one-off') {
      if (
        closure.day === targetDay &&
        closure.month === targetMonth &&
        closure.year === targetYear &&
        closure.is_open === false
      ) {
        return { isClosed: true, closureName: closure.name || 'Closed' };
      }
    }

    // Check recurring closures (e.g., "First Monday of January")
    if (closure.type === 'customer-holiday' || closure.type === 'hopdrive-holiday' || closure.type === 'federal-holiday') {
      const closureMonth = closure.month;
      const closureDayOfWeek = closure.day_of_week;
      const closureWeekOfMonth = closure.week_of_month;

      // Match month
      if (closureMonth !== targetMonth) continue;

      // Match day of week
      if (closureDayOfWeek !== targetDayOfWeek) continue;

      // Calculate which week of the month this date is
      const firstDayOfMonth = targetDate.startOf('month');
      const firstOccurrence = firstDayOfMonth.day(targetDayOfWeek);

      // Adjust if first occurrence is in previous month
      const adjustedFirstOccurrence = firstOccurrence.month() !== firstDayOfMonth.month()
        ? firstOccurrence.add(1, 'week')
        : firstOccurrence;

      // Calculate target week
      const targetWeekDate = adjustedFirstOccurrence.add((closureWeekOfMonth || 1) - 1, 'week');

      if (targetWeekDate.isSame(targetDate, 'day')) {
        return { isClosed: true, closureName: closure.name || 'Holiday' };
      }

      // Handle recurring (no year specified) - check pattern match for any year
      if (!closure.year && closureWeekOfMonth !== null) {
        if (targetWeekDate.isSame(targetDate, 'day')) {
          return { isClosed: true, closureName: closure.name || 'Holiday' };
        }
      }
    }
  }

  return { isClosed: false };
};

/**
 * Hook to get business hours for timeline display
 *
 * @param locationId - The location ID to fetch business hours for
 * @param selectedDate - The date to get hours for (format: YYYY-MM-DD)
 * @param timezoneStr - Timezone string (e.g., 'America/Los_Angeles')
 * @returns Timeline business hours with open/close times and closure status
 */
export const useBusinessHoursForTimeline = (
  locationId: string | undefined,
  selectedDate: string,
  timezoneStr: string = 'America/Los_Angeles'
): TimelineBusinessHours => {
  // Standard (hopdrive-managed) business hours differ from self-managed.
  const isStandardTier = !useValetSelfManaged();

  // Fetch business hours for this location
  const { data, loading, error } = useGetLocationBusinessHoursQuery({
    variables: { locationId: locationId as any },
    skip: !locationId,
  });

  const businessHours = useMemo((): TimelineBusinessHours => {
    // If no location ID, use defaults
    if (!locationId) {
      return {
        openTime: DEFAULT_OPEN_TIME,
        closeTime: DEFAULT_CLOSE_TIME,
        isClosed: false,
        isUsingDefaults: true,
      };
    }

    // If loading or error, use defaults temporarily
    if (loading || error || !data?.businesshours) {
      if (error) {
        console.error('❌ useBusinessHoursForTimeline: Error fetching business hours', error);
      }
      return {
        openTime: DEFAULT_OPEN_TIME,
        closeTime: DEFAULT_CLOSE_TIME,
        isClosed: false,
        isUsingDefaults: true,
      };
    }

    const allBusinessHours = data.businesshours as Businesshours[];

    // Step 1: Check for closures
    const closures = allBusinessHours.filter(
      (h) =>
        h.type === 'one-off' ||
        h.type === 'customer-holiday' ||
        h.type === 'hopdrive-holiday' ||
        h.type === 'federal-holiday'
    );

    const closureCheck = isDateClosed(selectedDate, closures, timezoneStr);
    if (closureCheck.isClosed) {
      return {
        openTime: DEFAULT_OPEN_TIME,
        closeTime: DEFAULT_CLOSE_TIME,
        isClosed: true,
        closureName: closureCheck.closureName,
        isUsingDefaults: false,
      };
    }

    // Step 1.25: Check for one-off modified hours (special hours for a specific date)
    // Note: .add(12, 'hours') is a workaround for timezone handling to ensure the correct day is selected
    const targetDateForOneOff = dayjs(selectedDate).add(12, 'hours').tz(timezoneStr);
    const oneOffModifiedHours = allBusinessHours.filter(
      (h) =>
        h.location_id?.toString() === locationId &&
        h.type === 'one-off' &&
        h.day === targetDateForOneOff.date() &&
        h.month === targetDateForOneOff.month() + 1 &&
        h.year === targetDateForOneOff.year() &&
        h.is_open === true
    );

    if (oneOffModifiedHours.length > 0) {
      const oneOffOpenTimes = oneOffModifiedHours
        .map((h) => h.open_time)
        .filter((t): t is string => t !== null)
        .sort();
      const oneOffCloseTimes = oneOffModifiedHours
        .map((h) => h.closed_time)
        .filter((t): t is string => t !== null)
        .sort();

      if (oneOffOpenTimes.length > 0 && oneOffCloseTimes.length > 0) {
        return {
          openTime: oneOffOpenTimes[0],
          closeTime: oneOffCloseTimes[oneOffCloseTimes.length - 1],
          isClosed: false,
          closureName: oneOffModifiedHours[0].name || undefined,
          isUsingDefaults: false,
        };
      }
    }

    // Step 1.5: Check for Sunday closure (Standard tier only)
    if (isStandardTier) {
      const targetDate = dayjs(selectedDate).add(12, 'hours').tz(timezoneStr);
      const isSunday = targetDate.day() === 0; // 0 = Sunday

      if (isSunday) {
        return {
          openTime: DEFAULT_OPEN_TIME,
          closeTime: DEFAULT_CLOSE_TIME,
          isClosed: true,
          closureName: 'Sunday - HopDrive is closed',
          isUsingDefaults: false,
        };
      }
    }

    // Step 2: Get day of week for selected date
    // Note: .add(12, 'hours') is a workaround for timezone handling to ensure the correct day is selected
    const targetDate = dayjs(selectedDate).add(12, 'hours').tz(timezoneStr);
    const dayOfWeek = targetDate.day(); // 0=Sunday, 6=Saturday

    // Step 2.5: Check if location is explicitly closed for this day of week
    const locationClosedForDay = allBusinessHours.find(
      (h) =>
        h.location_id?.toString() === locationId &&
        h.type === 'business-hours' &&
        h.day_of_week === dayOfWeek &&
        h.is_open === false
    );

    if (locationClosedForDay) {
      return {
        openTime: DEFAULT_OPEN_TIME,
        closeTime: DEFAULT_CLOSE_TIME,
        isClosed: true,
        closureName: locationClosedForDay.name || 'Location Closed',
        isUsingDefaults: false,
      };
    }

    // Step 3: Find business hours for this day
    // Priority: location-specific hours > default hours
    const locationSpecificHours = allBusinessHours.filter(
      (h) =>
        h.location_id?.toString() === locationId &&
        h.type === 'business-hours' &&
        h.day_of_week === dayOfWeek &&
        h.is_open
    );

    const defaultHours = allBusinessHours.filter(
      (h) =>
        !h.location_id &&
        h.type === 'default-hours' &&
        h.day_of_week === dayOfWeek &&
        h.is_open
    );

    const hoursToUse = locationSpecificHours.length > 0 ? locationSpecificHours : defaultHours;

    // Step 4: If no hours found or day is closed, return defaults
    if (hoursToUse.length === 0) {
      return {
        openTime: DEFAULT_OPEN_TIME,
        closeTime: DEFAULT_CLOSE_TIME,
        isClosed: false,
        isUsingDefaults: true,
      };
    }

    // Step 5: Calculate earliest open time and latest close time
    // (Handles multiple time blocks per day, e.g., 9-12, 1-5)
    const openTimes = hoursToUse
      .map((h) => h.open_time)
      .filter((t): t is string => t !== null)
      .sort();

    const closeTimes = hoursToUse
      .map((h) => h.closed_time)
      .filter((t): t is string => t !== null)
      .sort();

    if (openTimes.length === 0 || closeTimes.length === 0) {
      return {
        openTime: DEFAULT_OPEN_TIME,
        closeTime: DEFAULT_CLOSE_TIME,
        isClosed: false,
        isUsingDefaults: true,
      };
    }

    const earliestOpen = openTimes[0]; // First in sorted array
    const latestClose = closeTimes[closeTimes.length - 1]; // Last in sorted array


    return {
      openTime: earliestOpen,
      closeTime: latestClose,
      isClosed: false,
      isUsingDefaults: false,
    };
  }, [locationId, selectedDate, timezoneStr, data, loading, error, isStandardTier]);

  return businessHours;
};
